// topic-utils.ts — pure helpers shared by HelpPage + HelpTopicDetail.

import type { TopicCategory } from './types'

const CATEGORY_LABEL: Record<TopicCategory, string> = {
  'event-setup': 'Event setup',
  pricing: 'Pricing',
  etickets: 'E-tickets',
  kiosks: 'Kiosks',
  reporting: 'Reporting',
  attractions: 'Attractions',
  staff: 'Staff',
  operations: 'Operations',
}

export function categoryLabel(cat: TopicCategory | string): string {
  return (CATEGORY_LABEL as Record<string, string>)[cat] || cat
}

export function daysSinceVerified(iso: string, now = Date.now()): number {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY
  return Math.floor((now - t) / (24 * 60 * 60 * 1000))
}

export function isStale(iso: string, now = Date.now()): boolean {
  return daysSinceVerified(iso, now) > 90
}

// Step-completion localStorage persistence. One namespace per topic so
// marking a step complete on refund-transaction doesn't leak into
// configure-plans. Brief §test-matrix #8 — "step completion persists
// across reload".
const LS_KEY = (slug: string) => `butler-help:steps-completed:${slug}`

export function readCompletedSteps(slug: string): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(LS_KEY(slug))
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((s) => typeof s === 'string'))
  } catch {
    return new Set()
  }
}

export function writeCompletedSteps(slug: string, ids: Set<string>): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LS_KEY(slug), JSON.stringify([...ids]))
  } catch {
    // quota / private mode — silently drop; completion is a nice-to-have.
  }
}
